import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { authApi } from '../../api/auth.api.js';
import { membershipsApi } from '../../api/memberships.api.js';
import { Spinner } from '../../components/design/AtomFitUI.js';
import { useAuth } from '../../hooks/useAuth.js';
import type { MembershipTier } from '@atomfit/shared';

interface ProfileForm {
  firstName: string;
  lastName: string;
  phone: string;
  marketingEmailEnabled: boolean;
}

function emptyToNull(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function formatDate(value?: string | Date | null) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function Settings() {
  const { account, setAccount } = useAuth();
  const [profile, setProfile] = useState<ProfileForm>({
    firstName: account?.firstName ?? '',
    lastName: account?.lastName ?? '',
    phone: account?.phone ?? '',
    marketingEmailEnabled: account?.marketingEmailEnabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [membershipTier, setMembershipTier] = useState<MembershipTier | null>(null);

  useEffect(() => {
    setProfile({
      firstName: account?.firstName ?? '',
      lastName: account?.lastName ?? '',
      phone: account?.phone ?? '',
      marketingEmailEnabled: account?.marketingEmailEnabled !== false,
    });
  }, [account?.firstName, account?.lastName, account?.phone, account?.marketingEmailEnabled]);

  useEffect(() => {
    let cancelled = false;
    setMembershipTier(null);

    membershipsApi
      .my()
      .then((response) => {
        if (!cancelled) setMembershipTier(response.data.data[0]?.tier ?? null);
      })
      .catch(() => {
        if (!cancelled) setMembershipTier(null);
      });

    return () => {
      cancelled = true;
    };
  }, [account?.id]);

  const fullName = useMemo(
    () => [account?.firstName, account?.lastName].filter(Boolean).join(' '),
    [account],
  );
  const hasChanges =
    profile.firstName !== (account?.firstName ?? '') ||
    profile.lastName !== (account?.lastName ?? '') ||
    profile.phone !== (account?.phone ?? '') ||
    profile.marketingEmailEnabled !== (account?.marketingEmailEnabled !== false);

  async function saveProfile(e?: React.FormEvent) {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    if (profile.phone.trim() && !isValidPhoneNumber(profile.phone)) {
      setError(translate('pages.customer.settings.save_profile.set_error.phone'));
      setSaving(false);
      return;
    }
    try {
      const res = await authApi.updateMe({
        firstName: emptyToNull(profile.firstName),
        lastName: emptyToNull(profile.lastName),
        phone: emptyToNull(profile.phone),
        marketingEmailEnabled: profile.marketingEmailEnabled,
      });
      setAccount(res.data.account);
      setNotice(translate('pages.customer.settings.save_profile.set_notice.profile_save'));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pages.customer.settings.save_profile.set_error.error_profile_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function requestPasswordReset() {
    if (!account?.email) return;
    setSendingReset(true);
    setError(null);
    setNotice(null);
    try {
      await authApi.requestPasswordReset(account.email);
      setNotice(
        translate('pages.customer.settings.request_password_reset.set_notice.password_email'),
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pages.customer.settings.request_password_reset.set_error.error_password'),
      );
    } finally {
      setSendingReset(false);
    }
  }

  return (
    <>
      {notice && (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 0,
            background: 'rgba(74,222,128,0.1)',
            color: 'var(--af-success)',
            fontSize: 13,
            marginBottom: 18,
          }}
        >
          {notice}
        </div>
      )}
      {error && (
        <div className="af-error" style={{ marginBottom: 18 }}>
          {error}
        </div>
      )}

      <div className="af-settings-grid">
        <section className="af-card" style={{ padding: 22 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800 }}>
            {translate('pages.customer.settings.settings.h2.profile')}
          </h2>
          <br />
          <form onSubmit={(e) => void saveProfile(e)} style={{ display: 'grid', gap: 14 }}>
            <label className="af-label">
              {translate('pages.customer.settings.settings.label_label.email')}

              <input className="af-input" type="email" value={account?.email ?? ''} disabled />
            </label>
            <div className="af-settings-form-row">
              <label className="af-label">
                {translate('pages.customer.settings.settings.label_label.name')}

                <input
                  className="af-input"
                  type="text"
                  value={profile.firstName}
                  maxLength={100}
                  onChange={(e) =>
                    setProfile((current) => ({ ...current, firstName: e.target.value }))
                  }
                  placeholder={translate(
                    'pages.customer.settings.settings.input_input.placeholder_content',
                  )}
                />
              </label>
              <label className="af-label">
                {translate('pages.customer.settings.settings.label_label.last_name')}

                <input
                  className="af-input"
                  type="text"
                  value={profile.lastName}
                  maxLength={100}
                  onChange={(e) =>
                    setProfile((current) => ({ ...current, lastName: e.target.value }))
                  }
                  placeholder={translate(
                    'pages.customer.settings.settings.input_input.placeholder_create',
                  )}
                />
              </label>
            </div>
            <label className="af-label">
              {translate('pages.customer.settings.settings.label_label.phone')}

              <PhoneInput
                className="af-phone-input"
                defaultCountry="CZ"
                international
                countryCallingCodeEditable
                value={profile.phone}
                onChange={(value) => setProfile((current) => ({ ...current, phone: value ?? '' }))}
                placeholder={'+420 777 123 456'}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
              <button
                className="af-outline-btn"
                type="button"
                disabled={!hasChanges || saving}
                onClick={() =>
                  setProfile({
                    firstName: account?.firstName ?? '',
                    lastName: account?.lastName ?? '',
                    phone: account?.phone ?? '',
                    marketingEmailEnabled: account?.marketingEmailEnabled !== false,
                  })
                }
                style={{ padding: '11px 16px' }}
              >
                {translate('pages.customer.settings.settings.button_outline_btn.cancel')}
              </button>
              <button
                className="af-primary-btn"
                type="submit"
                disabled={!hasChanges || saving}
                style={{ padding: '11px 18px', minWidth: 112 }}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate('pages.customer.settings.settings.button_primary_btn.save')
                )}
              </button>
            </div>
          </form>
        </section>

        <aside style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <section className="af-card" style={{ padding: 20 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.customer.settings.settings.h2.heading')}
            </h2>
            <InfoRow
              label={translate('pages.customer.settings.settings.info_row.name')}
              value={fullName || '-'}
            />

            <InfoRow
              label={translate('pages.customer.settings.settings.info_row.label')}
              value={formatDate(account?.createdAt)}
            />

            {/* <InfoRow label="Referral kód" value={account?.referralCode ?? '-'} mono /> */}
            <InfoRow
              label={translate('pages.customer.settings.settings.info_row.membership')}
              value={formatMembershipTier(membershipTier)}
            />
          </section>

          <section className="af-card" style={{ padding: 20 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.customer.settings.settings.h2.email_campaign')}
            </h2>
            <p
              style={{
                margin: '0 0 14px',
                color: 'var(--af-muted)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {translate('pages.customer.settings.settings.p.booking')}
            </p>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <input
                type="checkbox"
                checked={profile.marketingEmailEnabled && !account?.marketingDeliverabilityBlocked}
                disabled={Boolean(account?.marketingDeliverabilityBlocked)}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    marketingEmailEnabled: event.target.checked,
                  }))
                }
              />

              {translate('pages.customer.settings.settings.label.email_campaign')}
            </label>
            {account?.marketingDeliverabilityBlocked && (
              <p style={{ margin: '10px 0 0', color: 'var(--af-warning)', fontSize: 12 }}>
                {translate('pages.customer.settings.settings.p.delivery')}
              </p>
            )}
            <button
              className="af-primary-btn"
              type="button"
              disabled={!hasChanges || saving || Boolean(account?.marketingDeliverabilityBlocked)}
              onClick={() => void saveProfile()}
              style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
            >
              {saving ? (
                <Spinner />
              ) : (
                translate('pages.customer.settings.settings.button_primary_btn.email_save')
              )}
            </button>
          </section>

          <section className="af-card" style={{ padding: 20 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.customer.settings.settings.h2.heading_2')}
            </h2>
            <p
              style={{
                margin: '0 0 16px',
                color: 'var(--af-muted)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {translate('pages.customer.settings.settings.p.password_email')}
            </p>
            <button
              className="af-outline-btn"
              type="button"
              disabled={sendingReset}
              onClick={() => void requestPasswordReset()}
              style={{ width: '100%', padding: '12px 14px' }}
            >
              {sendingReset ? (
                <Spinner />
              ) : (
                translate('pages.customer.settings.settings.button_outline_btn.password')
              )}
            </button>
          </section>
        </aside>
      </div>
    </>
  );
}

function formatMembershipTier(tier: MembershipTier | null) {
  if (!tier) return translate('pages.customer.settings.format_membership_tier.copy');

  return tier.charAt(0) + tier.slice(1).toLowerCase();
}

function InfoRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ padding: '11px 0', borderTop: '1px solid rgba(124, 118, 106, 0.18)' }}>
      <div
        style={{
          color: 'var(--af-dim)',
          fontSize: 11,
          fontWeight: 800,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        className={mono ? 'af-mono' : undefined}
        style={{
          color: 'var(--af-card-text)',
          fontSize: 13,
          fontWeight: 800,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </div>
    </div>
  );
}
